/*
******************************************************************************
Functions related to navigation 
******************************************************************************
*/

//@ts-nocheck
// Toggle preceding from @ts-nocheck to @ts-check to enable TypeScript checking

import { buildDamageIssues, clearStorage } from "./storage.js";
import { isVisible, resetTextValue, consoleMsg, getStoredValue, isLiveData, logDataMode } from "./utility.js";
import { generateInspectionPDF } from "./pdf-main.js";
import { combinedVehicleDetails, updateCombinedVehicleDetails, devModePhotosValidation } from "./app-data.js";
import { PhotoManager } from "../classes/photo-manager";
import { damageMgr, inspectionMgr } from "../web-components";

// prettier-ignore
export {
   next,
   previous,
   restoreFromLocalStorage,
   showDiv, hideDiv,
   showTextArea,
   handleSubmit,
   wireIssueTextAlert,
   buildSubmissionData,
   navsSubmit,
   displaySubmissionMessage,
   prefillVendorDates,
   showToast,
   base64DataUrlToBlob,
 };

// Make sure our app references are using the global app object associated with window
const app = window.app;

// #region JSDoc
/**
 * Navigates to the next page using the ion-router component.
 * @param {string} page - The page to navigate to.
 */
// #endregion
function next(page) {
	const router = /** @type {any} */ (document.querySelector("ion-router"));
	if (router) {
		router.push(page);
	}
}

// Wires up Issue Text alert behavior
function wireIssueTextAlert(alertId, deleteButtonPrefix = "deletePhotoButton") {
	const issueTextAlert = document.getElementById(alertId);
	if (!issueTextAlert) return;

	// NEW: ensure buttons exist (no JSON parsing)
	if (!issueTextAlert.buttons || issueTextAlert.buttons.length === 0) {
		issueTextAlert.buttons = [
			{ text: "Cancel", role: "cancel", id: "issue-cancel" },
			{ text: "OK", role: "confirm", id: "issue-ok" },
		];
	}

	issueTextAlert.addEventListener("ionAlertDidDismiss", (ev) => {
		const role = ev.detail.role;
		const { textId, divId, textArea } = issueTextAlert.data || {};

		if (role === "confirm") {
			if (divId) {
				const deleteBtns = document.querySelectorAll(`#${divId} ion-button[id^="${deleteButtonPrefix}"]`);
				deleteBtns.forEach((btn) => btn.click());
			}

			// clear issue text and mark as deleted
			const issueTextArea = textArea || (textId ? document.getElementById(textId) : null);
			if (issueTextArea && issueTextArea.value) {
				issueTextArea.value = "";

				// NEW: also clear any persisted value so it doesn't get restored later
				try {
					localStorage.removeItem(textId);
				} catch (_) {}

				const deletionMarkerKey = `${textId}-issueTextDeleted`;
				localStorage.setItem(deletionMarkerKey, "true");
			}

			if (divId) {
				hideDiv(divId);
				const photoErrorId = `${divId}-photo-error`;
				if (document.getElementById(photoErrorId)) hideDiv(photoErrorId);
			}
		} else if (role === "cancel") {
			const radioGroup = document.querySelector("ion-radio-group");
			if (radioGroup) {
				radioGroup.value = "Issue";
				radioGroup.dispatchEvent(new Event("ionChange"));
			}
			if (divId) showDiv(divId);
		}
	});
}

/**
 * Handles form submission.
 */

async function handleSubmit(currentPage) {
	const storageKey = app.state.storageKey;
	// Strip out the page number from currentPage string.  If current page length 6 or more, page number > 10: slice two chars
	let page;
	if (currentPage.length < 6) {
		page = currentPage.slice(-1);
	} else {
		page = currentPage.slice(-2);
	}

	// Ensure app.state.form is correctly referenced
	if (!app.state.form) {
		console.error("Form is not defined");
		return;
	}

	const formData = new FormData(app.state.form);
	// Define two objects: one for retrieving keys and data from the form, and one to save to Ionic Storage
	// Key retrieval object
	const data = {};
	// Storage object
	let obj = {};
	obj[currentPage] = {};
	for (let keyValue of formData.entries()) {
		data[keyValue[0]] = keyValue[1];
		localStorage.setItem(String(keyValue[0]), String(keyValue[1]));
		const myKey = String(page + "-" + keyValue[0]);
		const myVal = String(keyValue[1]);
	}

	// Set starting storage key index to 1
	let index = 1;

	for (let i = 0; i < Object.keys(data).length; i++) {
		// Define constant to retrieve page elements
		const element = document.getElementById(Object.keys(data)[i]);
		const key = Object.keys(data)[i];
		const value = Object.values(data)[i];
		let strValue;

		if (element.tagName === "DIV") {
			// Normalize whitespace: replace multiple spaces/newlines with single space and trim
			strValue = element.firstElementChild.textContent.replace(/\s+/g, " ").trim();
			obj[currentPage][index] = {
				id: key,
				value: value,
				string: strValue,
				index: index,
				page: page,
				problemValue: element.dataset.problemValue,
			};
			index++;
			// If present, get the value of the ion-textarea
		} else if (element.tagName === "ION-TEXTAREA" && element.value !== "") {
			obj[currentPage][index] = {
				id: key,
				value: value,
				string: "",
				index: index,
				page: page,
			};
			index++;
			// If present, get the value of the ion-datetime
		} else if (element.tagName === "ION-DATETIME" && element.value) {
			// Strip out the time from the date string
			const dateValue = value.slice(0, 10);
			obj[currentPage][index] = {
				id: key,
				value: dateValue,
				string: element.id,
				index: index,
				page: page,
			};
			index++;
			// Get value of ion-range if present
		} else if (element.tagName === "ION-RANGE") {
			strValue = element.dataset.string;
			obj[currentPage][index] = {
				id: key,
				value: value,
				string: strValue,
				index: index,
				page: page,
			};
			index++;
			// Get value of any present ion-input components
		} else if (element.tagName === "ION-INPUT" || element.tagName === "INPUT") {
			obj[currentPage][index] = {
				id: key,
				value: value,
				string: key,
				index: index,
				page: page,
			};
			index++;
			// Get value of any present ion-select components
		} else if (element.tagName === "ION-SELECT") {
			obj[currentPage][index] = {
				id: key,
				value: value,
				string: key,
				index: index,
				page: page,
				problemValue: element.dataset.problemValue,
			};
			index++;
			// Get value of any present ion-radio-group components
		} else if (element.tagName === "ION-RADIO-GROUP") {
			obj[currentPage][index] = {
				id: key,
				value: value,
				string: key,
				index: index,
				page: page,
			};
			index++;
		}
	}

	// Persist data in Ionic Storage
	await store.set(`${app.state.storageKey}_${currentPage}`, obj);
}

// ============================================================================
// Build final submission payload for Trevor
// ============================================================================
// navigation.js
async function buildSubmissionData() {
	const storageKey = app.state.storageKey;
	console.log("Building submission payload...");

	try {
		// ───────────────────────────────────────────────
		// 1) Inspection photos (car-*)
		// ───────────────────────────────────────────────
		const inspectionImages = [];
		const keys = Object.keys(app.state.photoData || {});
		const carKeys = keys.filter((k) => k.includes("car-"));
		consoleMsg(`buildSubmissionData: reading ${carKeys.length} inspection images...`);
		for (let i = 0; i < carKeys.length; i++) {
			const key = carKeys[i];
			const rec = await store.get(`${storageKey}_${key}`);
			if (rec && rec.photo) {
				inspectionImages.push(rec.photo);
			} else {
				console.warn(`buildSubmissionData: no photo found for key ${key}`);
			}
		}
		consoleMsg(`buildSubmissionData: ${inspectionImages.length} images ready`);

		// ───────────────────────────────────────────────
		// 2) PDF report
		// ───────────────────────────────────────────────
		let pdfReport = "";
		let pdfDataUri = await store.get(`${storageKey}_inspection-summary`);

		if (!pdfDataUri) {
			console.log("PDF not found in storage, generating now...");
			try {
				await generateInspectionPDF();
				await new Promise((resolve) => setTimeout(resolve, 500));
				pdfDataUri = await store.get(`${storageKey}_inspection-summary`);
			} catch (err) {
				console.error("Failed to generate PDF:", err);
			}
		}

		if (typeof pdfDataUri === "string" && pdfDataUri.startsWith("data:")) {
			pdfReport = pdfDataUri.replace(/^data:application\/pdf;base64,/, "");
		} else {
			console.warn("PDF data not available or invalid format:", typeof pdfDataUri);
		}

		// ───────────────────────────────────────────────
		// 3) Damage issues
		// ───────────────────────────────────────────────
		let damageIssues = [];
		try {
			const page2DataObj = await store.get(`${storageKey}_page2`);
			if (page2DataObj && page2DataObj.page2) {
				damageIssues = await buildDamageIssues(page2DataObj.page2);
			}
		} catch (err) {
			console.warn("Could not build damageIssues array:", err);
		}

		// ───────────────────────────────────────────────
		// 4) Status + attach to global
		// ───────────────────────────────────────────────
		let status = "Release";
		try {
			// Read status from user's actual input on signature page (page10)
			const page10Status = await store.get(`${storageKey}_page10`);
			status = page10Status?.page10?.[1]?.value || "Release";
		} catch (err) {
			console.warn("Could not resolve status:", err);
		}

		app.submissionData = {
			inspectionImages,
			pdfReport,
			status,
			damageIssues,
		};

		// ───────────────────────────────────────────────
		// 5) Include vendor date overrides only if changed
		// ───────────────────────────────────────────────
		try {
			const vendor = app.data?.navsData || {};
			const storedInsuranceDate = localStorage.getItem("insurance-date");
			const storedRegistrationDate = localStorage.getItem("registration-date");

			const includeInsurance = !!app.state.insuranceDateModified && !!storedInsuranceDate && storedInsuranceDate !== vendor.insuranceDate;

			const includeRegistration = !!app.state.registrationDateModified && !!storedRegistrationDate && storedRegistrationDate !== vendor.registrationDate;

			if (includeInsurance) {
				app.submissionData.insuranceDate = convertDateToNavs(storedInsuranceDate);
			}
			if (includeRegistration) {
				app.submissionData.registrationDate = convertDateToNavs(storedRegistrationDate);
			}

			if (includeInsurance || includeRegistration) {
				consoleMsg("Including vendor date overrides:", {
					insuranceDate: app.submissionData.insuranceDate,
					registrationDate: app.submissionData.registrationDate,
				});
			}
		} catch (err) {
			console.warn("Error comparing vendor date overrides:", err);
		}

		// ───────────────────────────────────────────────
		// 5b) Mileage & License Plate — include only if changed
		// ───────────────────────────────────────────────
		try {
			const page1 = await store.get(`${storageKey}_page1`);
			const page10 = await store.get(`${storageKey}_page10`);

			/*
			Baselines come from vehicle-info-table, the same source details.js seeds the
			inputs from. dataTables is a read-only snapshot of what NAVS passed and no
			longer tracks edits, so it stays a valid baseline for the whole session.
			*/
			const vehicleInfo = app.data?.dataTables?.["vehicle-info-table"] ?? {};
			const navsStarting = vehicleInfo["Starting Mileage"];
			const userStarting = getStoredValue(page1, "page1", "starting-mileage");
			const userEnding = getStoredValue(page10, "page10", "ending-mileage");

			// Normalize NAVS and user values to numbers
			const navsStartingNum = Number(String(navsStarting ?? "").trim()) || 0;
			const userStartingNum = Number(String(userStarting ?? "").trim()) || 0;
			const userEndingNum = Number(String(userEnding ?? "").trim()) || 0;

			// Include starting mileage only if changed
			if (userStartingNum !== navsStartingNum) {
				consoleMsg(`STARTING MILEAGE CHANGED: SUBMITTING ${userStartingNum}`);
				app.submissionData.startingMileage = userStartingNum;
			}
			app.submissionData.endingMileage = userEndingNum;

			// Include license plate only if changed from what NAVS passed
			const navsPlate = String(vehicleInfo["License Plate Number"] ?? "").trim();
			const userPlate = String(getStoredValue(page1, "page1", "license-plate-number")).trim();

			if (userPlate && userPlate !== navsPlate) {
				consoleMsg(`LICENSE PLATE CHANGED: SUBMITTING ${userPlate}`);
				app.submissionData.plate = userPlate;
			}
		} catch (err) {
			console.warn("Mileage / license plate comparison failed:", err);
		}

		consoleMsg("Submission data ready:", {
			...app.submissionData,
			pdfReport: pdfReport ? `[PDF data: ${pdfReport.length} chars]` : "null",
		});
	} catch (err) {
		consoleMsg("Error building submission data:", err);
	}
}

async function navsSubmit() {
	try {
		// Check if we're working with NAVS data. If not, simulate data submission locally without sending to NAVS endpoint.
		if (!logDataMode("navsSubmit")) {
			await simulateFormDataSubmission();
			return { success: true, payload: null };
		}

		const navs = app.data.navsData || {};
		const api = navs.apiLink;
		if (!api) {
			consoleMsg("ERROR: navsData.apiLink missing");
			return;
		}

		// Get the actual image data (now also pulling optional date overrides)
		const { inspectionImages, pdfReport, status, damageIssues, insuranceDate, registrationDate, startingMileage, endingMileage, plate } = app.submissionData || {};

		const pdf = app.submissionData?.pdfReport || null;

		// Create FormData object
		const formData = new FormData();

		// Append the required fields to FormData
		formData.append("activity_id", navs.activity_id);
		formData.append("direction", navs.direction);
		formData.append("status", status || "Release");

		// Optional vendor date overrides (only if present in submissionData)
		if (insuranceDate) formData.append("insuranceDate", insuranceDate);
		if (registrationDate) formData.append("registrationDate", registrationDate);

		// Mileage — only sent if different from NAVS baseline (startingMileage) or always (endingMileage)
		if (startingMileage !== undefined) formData.append("startingMileage", startingMileage);
		if (endingMileage !== undefined) formData.append("endingMileage", endingMileage);

		// License plate — only sent if the inspector changed what NAVS passed
		if (plate !== undefined) formData.append("plate", plate);

		// Convert Base64 → Blob before appending
		if (inspectionImages && inspectionImages.length > 0) {
			inspectionImages.forEach((image, index) => {
				const blob = base64DataUrlToBlob(image, "image/jpeg");
				formData.append(`inspectionImages[${index}]`, blob, `inspection-${index}.jpg`);
			});
		}

		// Send damageIssues as bracketed keys (no stringify) and convert photos to Blobs
		if (Array.isArray(damageIssues) && damageIssues.length > 0) {
			damageIssues.forEach((issue, i) => {
				if (issue && typeof issue === "object" && !Array.isArray(issue)) {
					for (const [k, v] of Object.entries(issue)) {
						if (k === "photo") {
							let src = v;
							let mime = "image/jpeg";
							if (typeof src === "string" && !src.startsWith("data:")) {
								if (src.startsWith("/9j/")) src = "data:image/jpeg;base64," + src;
								else if (src.startsWith("iVBOR")) {
									src = "data:image/png;base64," + src;
									mime = "image/png";
								}
							}
							const blob = base64DataUrlToBlob(src, mime);
							formData.append(`damageIssues[${i}][photo]`, blob, `damage-${i}.${mime === "image/png" ? "png" : "jpg"}`);
						} else if (k === "photos") {
							if (Array.isArray(v)) {
								v.forEach((s, j) => {
									let src = s,
										mime = "image/jpeg";
									if (!src.startsWith("data:")) {
										if (src.startsWith("/9j/")) src = "data:image/jpeg;base64," + src;
										else if (src.startsWith("iVBOR")) {
											src = "data:image/png;base64," + src;
											mime = "image/png";
										}
									}
									const blob = base64DataUrlToBlob(src, mime);
									formData.append(`damageIssues[${i}][photos][${j}]`, blob, `damage-${i}-${j}.${mime === "image/png" ? "png" : "jpg"}`);
								});
							} else if (typeof v === "string") {
								let src = v,
									mime = "image/jpeg";
								if (!src.startsWith("data:")) {
									if (src.startsWith("/9j/")) src = "data:image/jpeg;base64," + src;
									else if (src.startsWith("iVBOR")) {
										src = "data:image/png;base64," + src;
										mime = "image/png";
									}
								}
								const blob = base64DataUrlToBlob(src, mime);
								formData.append(`damageIssues[${i}][photos]`, blob, `damage-${i}.${mime === "image/png" ? "png" : "jpg"}`);
							} else {
								formData.append(`damageIssues[${i}][photos]`, v ?? "");
							}
						} else {
							formData.append(`damageIssues[${i}][${k}]`, v ?? "");
						}
					}
				} else {
					formData.append(`damageIssues[${i}]`, issue ?? "");
				}
			});
		}

		// Append PDF report
		if (pdf) {
			const pdfBlob = base64DataUrlToBlob(pdf, "application/pdf");
			formData.append("pdfReport", pdfBlob, "inspection-report.pdf");
		} else {
			throw new Error("PDF report missing or failed to generate");
		}

		// Send the FormData object with fetch
		// IMPORTANT: Do NOT set Content-Type header - browser will set it automatically with boundary
		const resp = await fetch(api, {
			method: "POST",
			body: formData, // Send FormData directly, not JSON.stringify
		});

		// Capture response body as text once; parse as JSON separately to avoid stream locking
		const text = await resp.text();
		let payload;
		try {
			//payload = await resp.json();
			payload = JSON.parse(text);
		} catch {
			//payload = await resp.text();
			payload = text;
		}

		consoleMsg(resp.ok ? "SUCCESS! Trevor test response:" : `FAILED! Trevor test failed: ${resp.status} ${resp.statusText}`, payload);
		return { success: resp.ok, status: resp.status, payload };
	} catch (err) {
		consoleMsg("Trevor test threw:", err);
		//return { success: false, payload: String(err) };
		return { success: false, payload: { errors: [{ message: err.message, type: "danger" }] } };
	}
}

function base64DataUrlToBlob(base64DataUrl, defaultMimeType = "image/jpeg") {
	base64DataUrl = base64DataUrl.trim();
	let base64Data, mimeType;
	if (base64DataUrl.includes(",")) {
		const match = base64DataUrl.match(/^data:([^;,]+)/);
		mimeType = match ? match[1] : defaultMimeType;
		base64Data = base64DataUrl.split(",")[1];
	} else {
		mimeType = defaultMimeType;
		base64Data = base64DataUrl;
	}
	const byteCharacters = atob(base64Data);
	const byteNumbers = new Array(byteCharacters.length);
	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}
	const byteArray = new Uint8Array(byteNumbers);
	return new Blob([byteArray], { type: mimeType });
}

async function simulateFormDataSubmission() {
	consoleMsg("🟡 Dummy mode: submitting to mock API endpoint");

	const { inspectionImages, pdfReport, status, damageIssues, insuranceDate, registrationDate, startingMileage, endingMileage, plate } = app.submissionData || {};
	const navs = app.data.navsData || {};
	const formData = new FormData();

	// Core fields — activity_id gets timestamp suffix to avoid overwriting same DB record on each test run
	formData.append("activity_id", `${navs.activity_id}_${Date.now()}`);
	formData.append("direction", navs.direction);
	formData.append("status", status || "Release");

	if (insuranceDate) formData.append("insuranceDate", insuranceDate);
	if (registrationDate) formData.append("registrationDate", registrationDate);

	if (startingMileage !== undefined) formData.append("startingMileage", startingMileage);
	if (endingMileage !== undefined) formData.append("endingMileage", endingMileage);
	if (plate !== undefined) formData.append("plate", plate);

	// Append inspection images
	(inspectionImages || []).forEach((img, i) => {
		const blob = base64DataUrlToBlob(img, "image/jpeg");
		formData.append(`inspectionImages[${i}]`, blob, `inspection-${i}.jpg`);
	});

	// Append damage issues
	(damageIssues || []).forEach((issue, i) => {
		if (issue && typeof issue === "object") {
			for (const [k, v] of Object.entries(issue)) {
				// Convert any Base64 image data into a Blob so it uploads like inspection images
				if (k === "photos") {
					if (Array.isArray(v)) {
						v.forEach((s, j) => {
							let src = s,
								mime = "image/jpeg";
							if (!src.startsWith("data:")) {
								if (src.startsWith("/9j/")) src = "data:image/jpeg;base64," + src;
								else if (src.startsWith("iVBOR")) {
									src = "data:image/png;base64," + src;
									mime = "image/png";
								}
							}
							const blob = base64DataUrlToBlob(src, mime);
							formData.append(`damageIssues[${i}][photos][${j}]`, blob, `damage-${i}-${j}.${mime === "image/png" ? "png" : "jpg"}`);
						});
					} else if (typeof v === "string") {
						let src = v,
							mime = "image/jpeg";
						if (!src.startsWith("data:")) {
							if (src.startsWith("/9j/")) src = "data:image/jpeg;base64," + src;
							else if (src.startsWith("iVBOR")) {
								src = "data:image/png;base64," + src;
								mime = "image/png";
							}
						}
						const blob = base64DataUrlToBlob(src, mime);
						formData.append(`damageIssues[${i}][photos]`, blob, `damage-${i}.${mime === "image/png" ? "png" : "jpg"}`);
					} else {
						formData.append(`damageIssues[${i}][photos]`, v ?? "");
					}
				} else {
					formData.append(`damageIssues[${i}][${k}]`, v ?? "");
				}
			}
		}
	});

	// Append PDF
	if (pdfReport) {
		const pdfBlob = base64DataUrlToBlob(pdfReport, "application/pdf");
		formData.append("pdfReport", pdfBlob, "inspection-report.pdf");
	} else {
		app.settings.mockMessageType = "danger";
		throw new Error("Missing PDF report");
	}

	// Log everything (non-binary truncated)
	console.log("Simulated FormData (dummy mode):");
	for (const [k, v] of formData.entries()) {
		if (v instanceof Blob) {
			console.log(`${k} → [Blob ${v.type}, ${v.size} bytes]`);
		} else {
			console.log(`${k} →`, String(v).slice(0, 120));
		}
	}

	await fetch("/mock-api/api/submit.php", { method: "POST", body: formData });
}

// --- Helper: find PDF and convert to a data URL if needed ---
async function resolvePdfDataURL() {
	// Likely homes based on your app structure:
	const sd = (window.app && (window.app.submissionData || window.app.data)) || {};
	let pdf = sd.pdfReport || window.pdfReport || window.pdf || null;

	if (!pdf) {
		consoleMsg("⚠️ No PDF found (submission will send null pdfReport).");
		return null;
	}

	// If already a string (e.g., data URL), return as-is
	if (typeof pdf === "string") return pdf;

	// If it's a Blob/File, convert to a data URL so JSON.stringify can handle it
	if (pdf instanceof Blob) {
		return await blobToDataURL(pdf);
	}

	// If it’s a URL (http/https/blob), try to fetch then convert to data URL
	if (typeof pdf === "object" && pdf?.src && typeof pdf.src === "string") {
		try {
			const res = await fetch(pdf.src);
			if (res.ok) {
				const b = await res.blob();
				return await blobToDataURL(b);
			}
		} catch (_) {
			/* ignore */
		}
	}

	// Unknown type: best-effort stringify
	try {
		return String(pdf);
	} catch {
		consoleMsg("⚠️ Could not serialize PDF; sending null.");
		return null;
	}
}

function blobToDataURL(blob) {
	return new Promise((resolve, reject) => {
		const r = new FileReader();
		r.onload = () => resolve(r.result);
		r.onerror = reject;
		r.readAsDataURL(blob);
	});
}

/**
 * Navigates to the previous page using the ion-router component.
 * @param {string} page - The page to navigate to.
 */
async function previous(page) {
	// Store current page form values to local storage so they can later be restored
	await handleSubmit(app.state.currentPage);
	//}
	/** @type {any} */
	const router = document.querySelector("ion-router");
	if (router) {
		await router.push(page, "back");
	}
}

// Restore values from Ionic storage (NOTE: overall function not async because sub anonymous functions are)
// NOTE: LOGICALLY THIS PROBABLY MAKES MORE SENSE TO DEFINE IN STORAGE MODULE.  CONSIDER MOVING.
async function restoreFromLocalStorage() {
	// #region JSDoc
	/**
	 * @typedef {HTMLElement & { name: string, value: string }} HTMLIonRadioGroupElement
	 * @typedef {HTMLElement & { value: string }} HTMLIonTextareaElement
	 * @typedef {HTMLElement & { value: string }} HTMLIonDatetimeElement
	 * @typedef {HTMLElement & { value: string }} HTMLIonRangeElement
	 * @typedef {HTMLElement & { checked: boolean, name: string, value: string }} HTMLIonRadioElement
	 */

	/**
	 * Map of selectors to types for query results
	 * @type {{
	 *   "ion-radio-group": NodeListOf<HTMLIonRadioGroupElement>,
	 *   "ion-textarea": NodeListOf<HTMLIonTextareaElement>,
	 *   "ion-datetime": NodeListOf<HTMLIonDatetimeElement>,
	 *   "ion-range": NodeListOf<HTMLIonRangeElement>,
	 *   "ion-input": NodeListOf<HTMLIonInputElement>
	 * }}
	 */
	// #endregion

	// Map of element types to query selectors defined in JSDoc type definition
	const elementTypeMap = {
		"ion-radio-group": document.querySelectorAll("ion-radio-group"),
		"ion-textarea": document.querySelectorAll("ion-textarea"),
		"ion-datetime": document.querySelectorAll("ion-datetime"),
		"ion-range": document.querySelectorAll("ion-range"),
		"ion-input": document.querySelectorAll("ion-input"),
	};

	// Variables referencing various ionic components
	const radioGroups = elementTypeMap["ion-radio-group"];
	const textAreas = elementTypeMap["ion-textarea"];
	const datePickers = elementTypeMap["ion-datetime"];
	const ionRange = elementTypeMap["ion-range"];
	const ionInputs = elementTypeMap["ion-input"];

	// Restore damage and inspection photos
	if (app.state.currentPage === "page2" || app.state.currentPage === "page11") {
		await damageMgr.restoreAllDamagePhotos();
	} else if (app.state.currentPage === "page9") {
		await inspectionMgr.restoreAllPhotos();
	}

	// Restore radio group values
	radioGroups.forEach(async (radioGroup) => {
		const storedValue = await localStorage.getItem(radioGroup.name);
		if (storedValue) {
			// Set the ion-radio-group value directly
			radioGroup.value = storedValue;

			// Find the ion-radio with the matching value
			/** @type {HTMLIonRadioElement | null} */
			const radioButton = radioGroup.querySelector(`ion-radio[value="${storedValue}"]`);

			if (radioButton) {
				// Ensure the ion-radio is marked as checked
				radioButton.checked = true;
				const div = radioButton.name + "-div";
				const dateTime = radioButton.name + "-date";
				const myDiv = document.getElementById(div);
				const myDate = document.getElementById(dateTime);
				const problemValue = radioGroup.closest("[data-problem-value]")?.dataset.problemValue;

				if (radioButton.value === problemValue) {
					if (myDiv) showDiv(div);
				} else {
					if (myDate) showDiv(div);
				}

				// Dispatch the ionChange event to update the UI
				radioButton.dispatchEvent(
					new CustomEvent("ionChange", {
						bubbles: true,
					}),
				);
			}
		}
	});

	// Restore datePickers: node list.  If greater than 0, date pickers present on page
	if (datePickers.length > 0) {
		datePickers.forEach((dt) => {
			const storedDateTime = localStorage.getItem(dt.id);
			if (storedDateTime) {
				dt.value = storedDateTime;
			}
		});
	}

	// Restore text area values
	if (textAreas) {
		textAreas.forEach((textArea) => {
			const storedText = localStorage.getItem(textArea.id);
			if (storedText) {
				textArea.value = storedText;
			}
		});
	}

	// Restore range values
	if (ionRange) {
		ionRange.forEach((range) => {
			const storedRange = localStorage.getItem(range.id);
			if (storedRange) {
				range.value = storedRange;
			}
		});
	}

	// Restore ion input values
	if (ionInputs) {
		ionInputs.forEach((input) => {
			const storedInput = localStorage.getItem(input.id);
			if (storedInput) {
				input.value = storedInput;
			}
		});
	}
}

function showDiv(div) {
	const myDiv = document.getElementById(div);
	// Check that myDiv references an actual page element
	if (myDiv) {
		myDiv.classList.remove("hidden");
	}
}

function hideDiv(div) {
	const myDiv = document.getElementById(div);
	// Check that myDiv references an actual page element
	if (myDiv) {
		myDiv.classList.add("hidden");
	}
}

function showTextArea() {
	// Use same page-number extraction logic as handleSubmit()
	const currentPage = app.state.currentPage;
	const pageNum = currentPage.length < 6 ? currentPage.slice(-1) : currentPage.slice(-2);
	const alertId = "issueTextAlert" + pageNum;
	const div = this.name + "-div";
	const photos = document.querySelectorAll("#" + div + " img");
	const text = this.name + "-text";
	const myText = document.getElementById(text);
	const dialog = document.getElementById(alertId);
	// Check if textarea exists for div
	if (myText) {
		if (this.value === "Issue") {
			showDiv(div);
		} else {
			// Check if text area has a value or damage photos have been taken
			if (myText.value || photos.length > 0) {
				resetTextValue(dialog, text, div);
			} else {
				hideDiv(div);
			}
		}
	}
}

async function displaySubmissionMessage(payload, requestSucceeded, httpStatus) {
	if (logDataMode("displaySubmissionMessage")) {
		// Non-JSON response (PHP error page, gateway error, etc.)
		if (typeof payload === "string") {
			const statusText = httpStatus ? ` (HTTP ${httpStatus})` : "";
			showToast(`Server returned an unexpected response${statusText}. Please try again or contact support.`, "danger");
			return;
		}

		const errors = Array.isArray(payload?.errors) ? payload.errors : [];
		const failureErrors = errors.filter((e) => e.type !== "success");

		// Any non-success errors present — map each to a user-friendly message
		if (failureErrors.length > 0) {
			const messages = failureErrors.map((e) => {
				if (e.type === "size") return "The inspection payload exceeded the server's upload size limit. Contact support.";
				if (e.type === "exception") return "A server error occurred. Please try again or contact support if this persists.";
				if (e.type === "encode") return "A server error occurred (response encoding failed). Please try again.";
				return e.message;
			});
			const messageList = ["<ul>", ...messages.map((m) => `<li>${m}</li>`), "</ul>"].join("");
			showToast(messageList, "danger");
			return;
		}

		// Success
		if (requestSucceeded) {
			clearStorage();
			localStorage.setItem("inspection-submitted", "true");
			const router = document.querySelector("ion-router");
			await router.push(app.routes.submissionComplete);
			return;
		}

		// Fallback — non-2xx with no usable error details
		const statusText = httpStatus ? ` (HTTP ${httpStatus})` : "";
		showToast(`Submission failed${statusText} with no details from the server. Please try again or contact support.`, "danger");
	} else {
		// Working with dummy data
		const successMessage = "Mock: Inspection submitted successfully!";
		// prettier-ignore
		let errorMessage = payload?.errors?.length
         ? ["Mock: Submission failed with the following errors:",
            "<ul>", ...payload.errors.map((error) => `<li>${error.message}</li>`), "</ul>"].join("")
         : "Mock: Submission failed (no error details).";
		if (app.settings.mockMessageType === "success") {
			clearStorage();
			/* "dummy" rather than "true" records the mode at submission time, when
			app.settings.liveData is reliably known. submission-complete.js reads it back
			to decide whether the dev-only flag-clearing shortcut is allowed. */
			localStorage.setItem("inspection-submitted", "dummy");
			const router = document.querySelector("ion-router");
			await router.push(app.routes.submissionComplete);
		} else {
			showToast(errorMessage, "danger");
		}
	}
}

async function submitSuccessAlert(message, dismissCallback) {
	const alert = document.createElement("ion-alert");
	alert.header = "Inspection Submitted";
	alert.message = message;
	alert.buttons = [{ text: "OK", role: "confirm" }];
	document.body.appendChild(alert);
	await alert.componentOnReady();
	await alert.present();
	alert.addEventListener(
		"ionAlertDidDismiss",
		() => {
			dismissCallback();
			alert.remove();
		},
		{ once: true },
	);
}

function showToast(message, type) {
	const toast = document.getElementById("submission-toast");
	const messageEl = document.getElementById("submission-toast-message");
	const dismissBtn = document.getElementById("submission-toast-dismiss");

	if (!toast || !messageEl || !dismissBtn) return;

	// Map type to your actual CSS class
	let toastClass = "";
	if (type === "danger") {
		toastClass = "error-toast";
	} else if (type === "success") {
		toastClass = "success-toast";
	}

	// Use innerHTML if message contains HTML tags (like <ul><li>), else use textContent
	if (typeof message === "string" && /<ul>|<li>|<br>/.test(message)) {
		messageEl.innerHTML = message;
	} else {
		messageEl.textContent = message;
	}
	toast.className = `submission-toast ${toastClass}`;

	// Show toast
	toast.classList.remove("hidden");
	consoleMsg("Toast message:", message, "Type:", type);

	// Wire up dismiss button
	dismissBtn.onclick = () => toast.classList.add("hidden");
}

// --- Helpers: dates ---
function convertDateToIso(s) {
	const m = String(s || "")
		.trim()
		.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
	if (!m) return null;
	const mm = m[1].padStart(2, "0");
	const dd = m[2].padStart(2, "0");
	const yyyy = m[3];
	return `${yyyy}-${mm}-${dd}`;
}

function convertDateToNavs(iso) {
	if (!iso) return null;
	const [year, month, day] = iso.split("T")[0].split("-");
	return `${month}/${day}/${year}`;
}

function todayIso() {
	const d = new Date();
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	const yyyy = d.getFullYear();
	return `${yyyy}-${mm}-${dd}`;
}

function prefillVendorDates() {
	const navs = app.data.navsData;
	if (!navs) return;

	// Use persistence as the guard: if a prior value exists, do nothing
	const insEl = document.getElementById("insurance-date");
	if (insEl && !localStorage.getItem("insurance-date") && navs.insuranceDate) {
		const iso = convertDateToIso(navs.insuranceDate);
		if (iso) insEl.value = iso;
	}

	const regEl = document.getElementById("registration-date");
	if (regEl && !localStorage.getItem("registration-date") && navs.registrationDate) {
		const iso = convertDateToIso(navs.registrationDate);
		if (iso) regEl.value = iso;
	}

	// Ensure dateState exists with expected shape
	app.data.dateState = app.data.dateState || {
		vendorDatesProvided: false,
		original: { insuranceDate: null, registrationDate: null },
		values: { insuranceDate: null, registrationDate: null },
		edited: { insuranceDate: false, registrationDate: false },
	};

	(function updateDateStateAfterPrefill() {
		const ins = (navs && navs.insuranceDate) || null;
		const reg = (navs && navs.registrationDate) || null;

		// Consider vendor dates "provided" only if at least one is non-empty
		const hasVendorDates = Boolean(ins || reg);
		app.data.dateState.vendorDatesProvided = hasVendorDates;

		// Record originals and initialize current values
		app.data.dateState.original.insuranceDate = ins;
		app.data.dateState.original.registrationDate = reg;

		app.data.dateState.values.insuranceDate = ins;
		app.data.dateState.values.registrationDate = reg;

		// Reset edited flags on prefill
		app.data.dateState.edited.insuranceDate = false;
		app.data.dateState.edited.registrationDate = false;
	})();
}

function trackVendorDateEdit(fieldName, newValue) {
	// Normalize falsy to null for stable comparisons
	const val = newValue || null;
	app.data.dateState.values[fieldName] = val;

	const original = app.data.dateState.original[fieldName] || null;
	app.data.dateState.edited[fieldName] = val !== original;
}
